import { useState, useMemo, useEffect, useRef } from "react";
import type { Page } from "../types";

import banner1 from "../assets/BannerPhotos_1.jpg";
import banner2 from "../assets/BannerPhotos_2.jpg";
import banner3 from "../assets/BannerPhotos_3.jpg";
import banner4 from "../assets/BannerPhotos_4.jpg";
import banner5 from "../assets/BannerPhotos_5.jpg";
import banner6 from "../assets/BannerPhotos_6.jpg";
import banner7 from "../assets/BannerPhotos_7.jpg";
import banner8 from "../assets/BannerPhotos_8.jpg";

const allBannerImages = [banner1, banner2, banner3, banner4, banner5, banner6, banner7, banner8];

function shuffleArray<T>(arr: T[]): T[] {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = a[i];
        a[i] = a[j]!;
        a[j] = tmp!;
    }
    return a;
}

export function Hero({ onNavigate }: { onNavigate: (p: Page) => void }) {
    const [showSetB, setShowSetB] = useState(false);
    const [paused, setPaused] = useState(false);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Shuffle once on mount: first 4 = set A, last 4 = set B
    const shuffled = useMemo(() => shuffleArray(allBannerImages), []);
    const setA = shuffled.slice(0, 4);
    const setB = shuffled.slice(4, 8);

    // Auto-cycle every 4s unless paused
    useEffect(() => {
        if (paused) {
            if (timerRef.current) clearInterval(timerRef.current);
            timerRef.current = null;
            return;
        }
        timerRef.current = setInterval(() => {
            setShowSetB((prev) => !prev);
        }, 6000);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [paused]);

    const handleMouseEnter = () => {
        setPaused(true);
        setShowSetB((prev) => !prev);
    };

    const handleMouseLeave = () => {
        setPaused(false);
    };

    return (
        <section className="pt-16 lg:pt-24 bg-gradient-to-b from-orange-50 to-white overflow-hidden">
            <div className="max-w-6xl mx-auto grid lg:grid-cols-2 items-center gap-8 lg:gap-12 px-6">
                {/* Left — text content */}
                <div className="pt-8 lg:py-20">
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1a3660] leading-tight mb-6">
                        Votre partenaire digital
                        <br />
                        <span className="text-[#f07020]">de proximité</span>
                    </h1>
                    <p className="text-lg sm:text-xl text-slate-500 max-w-xl mb-4 leading-relaxed">
                        Nous concevons des sites et applications sur mesure, professionnels et accessibles.
                        <br />
                        Nos solutions sont pensées pour les créateurs, les petits commerces, les artistes et les auto-entrepreneurs.<br />
                        Vous gardez facilement la main dessus: gérez vos textes, photos et produits simplement, en toute autonomie.
                    </p>
                </div>

                {/* Right — 2×2 photo grid with crossfade on hover */}
                <div
                    className="grid grid-cols-2 gap-3 lg:-mr-6 pb-8 lg:pb-0"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    {setA.map((imgA, i) => (
                        <div key={i} className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                            <img
                                src={imgA}
                                alt=""
                                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1200 ease-in-out"
                                style={{ opacity: showSetB ? 0 : 1, transitionDuration: '1200ms' }}
                            />
                            <img
                                src={setB[i]}
                                alt=""
                                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1200 ease-in-out"
                                style={{ opacity: showSetB ? 1 : 0, transitionDuration: '1200ms' }}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
